/**
 * Right panel - Inspector and controls
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Box, ScrollArea, Text, Divider } from "@mantine/core";
import {
  useViewerStore,
  useSelectedModuleInstanceId,
  useInstances,
  useEditLinksMode,
} from "../store";
import { useIsMobile } from "../hooks";
import { InstanceTree } from "../components/InstanceTree";
import { IKControls } from "./right/IKControls";
import { JointAxesSection } from "./right/JointAxesSection";
import { LocomotionSection } from "./right/LocomotionSection";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { ModuleAxesPanel } from "./right/ModuleAxesPanel";
import { ModuleInspector } from "./right/ModuleInspector";
import { EditLinksPanel } from "./right/EditLinksPanel";

const RIGHT_PANEL_DEFAULT_WIDTH = 300;
const RIGHT_PANEL_MIN_WIDTH = 240;
const VIEWER_MIN_WIDTH = 360;
const RIGHT_PANEL_WIDTH_STORAGE_KEY = "robot-compiler.viewer.right-panel-width";

export function RightPanel() {
  const guiElements = useViewerStore((state) => state.guiElements);
  const selectedModuleInstanceId = useSelectedModuleInstanceId();
  const instances = useInstances();
  const editLinksMode = useEditLinksMode();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelWidth, setPanelWidth] = useState(RIGHT_PANEL_DEFAULT_WIDTH);
  const panelWidthRef = useRef(RIGHT_PANEL_DEFAULT_WIDTH);
  // Which pointer is dragging the separator, so a second touch elsewhere
  // doesn't also resize the panel.
  const resizePointerIdRef = useRef<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  // On a phone this panel fills a bottom sheet, so there is no column edge
  // left to drag.
  const isMobile = useIsMobile();

  const rightPanelElements = Object.values(guiElements).filter(
    (el) => el.panel === "right" && el.container_uuid === "root"
  );

  const hasRightElements = rightPanelElements.length > 0;
  const hasSelectedModule = selectedModuleInstanceId !== null;
  const hasInstances = instances.length > 0;

  const clampPanelWidth = useCallback((requestedWidth: number): number => {
    const panelElement = panelRef.current;
    const containerElement = panelElement?.parentElement;
    const leftPanelElement = panelElement?.previousElementSibling?.previousElementSibling as HTMLElement | null;

    const containerWidth = containerElement?.clientWidth ?? window.innerWidth;
    const leftPanelWidth = leftPanelElement?.getBoundingClientRect().width ?? 0;
    const maxWidth = Math.max(RIGHT_PANEL_MIN_WIDTH, containerWidth - leftPanelWidth - VIEWER_MIN_WIDTH);

    return Math.round(Math.min(maxWidth, Math.max(RIGHT_PANEL_MIN_WIDTH, requestedWidth)));
  }, []);

  useEffect(() => {
    setPanelWidth((currentWidth) => {
      let desiredWidth = currentWidth;
      try {
        const saved = Number(localStorage.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY));
        if (Number.isFinite(saved) && saved > 0) {
          desiredWidth = saved;
        }
      } catch {
        // Ignore storage errors and keep default width.
      }
      return clampPanelWidth(desiredWidth);
    });
  }, [clampPanelWidth]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    const onWindowResize = () => {
      setPanelWidth((currentWidth) => clampPanelWidth(currentWidth));
    };
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [clampPanelWidth]);

  useEffect(() => {
    if (!isResizing) {
      return;
    }

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onPointerMove = (event: PointerEvent) => {
      const panelElement = panelRef.current;
      if (!panelElement || event.pointerId !== resizePointerIdRef.current) {
        return;
      }
      const panelRight = panelElement.getBoundingClientRect().right;
      const requestedWidth = panelRight - event.clientX;
      setPanelWidth(clampPanelWidth(requestedWidth));
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerId !== resizePointerIdRef.current) {
        return;
      }
      resizePointerIdRef.current = null;
      setIsResizing(false);
      try {
        localStorage.setItem(RIGHT_PANEL_WIDTH_STORAGE_KEY, String(panelWidthRef.current));
      } catch {
        // Ignore storage errors.
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isResizing, clampPanelWidth]);

  const handleResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizePointerIdRef.current = event.pointerId;
    setIsResizing(true);
  }, []);

  const handleResizeReset = useCallback(() => {
    setPanelWidth(clampPanelWidth(RIGHT_PANEL_DEFAULT_WIDTH));
    try {
      localStorage.removeItem(RIGHT_PANEL_WIDTH_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }, [clampPanelWidth]);

  return (
    <Box
      ref={panelRef}
      style={{
        width: isMobile ? "100%" : panelWidth,
        height: "100%",
        backgroundColor: "var(--mantine-color-body)",
        borderLeft: isMobile ? "none" : "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {!isMobile && (
        <Box
          role="separator"
          aria-label="Resize right panel"
          onPointerDown={handleResizeStart}
          onDoubleClick={handleResizeReset}
          style={{
            position: "absolute",
            left: -3,
            top: 0,
            bottom: 0,
            width: 6,
            cursor: "col-resize",
            // Otherwise a touch on the separator scrolls the panel instead.
            touchAction: "none",
            zIndex: 120,
            backgroundColor: isResizing ? "var(--mantine-color-blue-6)" : "transparent",
          }}
        />
      )}

      {isResizing && (
        <Box
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 110,
            cursor: "col-resize",
          }}
        />
      )}

      {/* Edit Links Mode Overlay */}
      {editLinksMode && <EditLinksPanel />}
      {/* Module axis editor, which writes the module rather than the scene */}
      <ModuleAxesPanel />
      {/* Module Tree Section */}
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Modules
        </Text>
      </Box>
      <Box
        p="sm"
        style={{
          borderBottom: "1px solid var(--mantine-color-gray-3)",
          maxHeight: hasInstances ? 200 : "auto",
          overflowY: "auto",
        }}
      >
        <InstanceTree />
      </Box>

      {/* Inspector Section */}
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        <Text size="lg" fw={600}>
          Inspector
        </Text>
      </Box>

      <ScrollArea style={{ flex: 1 }} p="sm">
        <SectionErrorBoundary name="Inspector">
              <ModuleInspector />
            </SectionErrorBoundary>

        {hasSelectedModule && hasRightElements && (
          <Divider my="md" label="Controls" labelPosition="center" />
        )}

        {hasRightElements && <RightPanelContent />}

        {/* IK Controls Section */}
        {hasInstances && (
          <>
            <Divider my="md" label="IK Controls" labelPosition="center" />
            <SectionErrorBoundary name="IK Controls">
              <IKControls />
            </SectionErrorBoundary>
          </>
        )}

        {/* Joint axis overview -- the servo-orientation check */}
        {hasInstances && (
          <>
            <Divider my="md" label="Joint Axes" labelPosition="center" />
            <SectionErrorBoundary name="Joint Axes">
              <JointAxesSection />
            </SectionErrorBoundary>
          </>
        )}

        {/* RL walking: home pose, forward direction, feet, and the train command */}
        {hasInstances && (
          <>
            <Divider my="md" label="Locomotion" labelPosition="center" />
            <SectionErrorBoundary name="Locomotion">
              <LocomotionSection />
            </SectionErrorBoundary>
          </>
        )}

      </ScrollArea>
    </Box>
  );
}

function RightPanelContent() {
  const guiElements = useViewerStore((state) => state.guiElements);
  const guiContainerChildren = useViewerStore((state) => state.guiContainerChildren);

  const rootChildren = guiContainerChildren["root"];
  if (!rootChildren) return null;

  const elements = Array.from(rootChildren)
    .map((uuid) => guiElements[uuid])
    .filter((el) => el && el.panel === "right")
    .sort((a, b) => a.order - b.order);

  return (
    <>
      {elements.map((element) => {
        switch (element.type) {
          case "folder":
            return (
              <Box key={element.uuid} mb="md">
                <FolderWithChildren element={element} />
              </Box>
            );
          default:
            return (
              <Box key={element.uuid}>
                <GuiElementInline element={element} />
              </Box>
            );
        }
      })}
    </>
  );
}

import { GuiFolder, GuiButton, GuiSlider, GuiDropdown, GuiCheckbox, GuiText } from "../components";
import type { GuiElement, GuiFolderElement } from "../types";

function FolderWithChildren({ element }: { element: GuiFolderElement }) {
  return <GuiFolder element={element} />;
}

function GuiElementInline({ element }: { element: GuiElement }) {
  switch (element.type) {
    case "button":
      return <GuiButton element={element} />;
    case "slider":
      return <GuiSlider element={element} />;
    case "dropdown":
      return <GuiDropdown element={element} />;
    case "checkbox":
      return <GuiCheckbox element={element} />;
    case "text":
      return <GuiText element={element} />;
    default:
      return null;
  }
}
